import { useCallback, useEffect, useMemo } from 'react';
import { PersistActiveItemsReducer, PersistItemsReducer, PersistProjectItemsReducer, PersistValueReducer } from 'features/Persist/reducerTypes';
import { IdNameSymbolResponse, ProjectResponse, ProjectTeamMemberResponse, TenantResponseModel, UserModel } from 'services/tenantManagementService';
import { StatusResponse } from 'features/StatusResponse/statusResponse';
import { colorToHex } from 'utils/colorHelper';
import { EntityPrefixEnum, getFormatedId } from 'utils/commonHelper';
import { formatNumberUtil } from 'utils/currencyUtils';
import { useSelector } from 'react-redux';
import { RootState } from 'base/reducer/reducer';
import { MapItem, convertToAbsoluteName, getSelectedItem } from 'components/Form/controls/MapPicker/MapPicker/helpers';
import { removeUndefinedItemsFromArray } from 'utils/arrayUtil';
import { persistTeamMembersAction } from 'containers/Projects/MyProjects/MaintainProject/Tabs/AssignTeamMembers/action';

type IdToName = {
	id: number
	name: string
}

const useIdToNameCallback = (persistedModel: PersistItemsReducer<IdToName> | undefined) => {
	return useCallback(
		(id: number | undefined) => {
			const item = id && persistedModel?.itemsMap[id];
			return item ? item.name : '';
		},
		[persistedModel]
	)
}

export const useProjectIsActiveNameCallback = useIdToNameCallback;
export const useTenantIsActiveCallback = useIdToNameCallback;
export const useStatusCallback = useIdToNameCallback;
export const usePartnerCallback = useIdToNameCallback;

export const useUsernameCallback = (persistedModel: PersistActiveItemsReducer<UserModel>) => {
	return useCallback(
		(id: number | undefined) => {
			const user = id && persistedModel.itemsMap[id];
			return user ? user.username : '';
		},
		[persistedModel]
	)
}

export const useUserFullNameCallback = (persistedModel: PersistActiveItemsReducer<UserModel>) => {
	return useCallback(
		(id: number | undefined) => {
			const user = id && persistedModel.itemsMap[id];
			return user ? `${user.firstName} ${user.lastName}` : '';
		},
		[persistedModel]
	)
}

export const useOrganizationalUnitCallback = () => {
	return useCallback(
		(id: number | undefined, options: MapItem[] = []) => {
			const selectedOption = getSelectedItem(id, options);
			if (selectedOption) {
				return convertToAbsoluteName(selectedOption, options);
			}

			return '';
		},
		[]
	)
}

const useUserIdCallback = (persistedModel: PersistProjectItemsReducer<ProjectTeamMemberResponse>) => {
	return useCallback(
		(id: number, projectId: number) => {
			const member = persistedModel.projectMap[projectId]?.itemsMap[id];
			return member && member.userId;
		},
		[persistedModel]
	)
}

export const useStatusColorCallback = <P extends StatusResponse>(persistedModel: PersistItemsReducer<P>) => {
	return useCallback(
		(id: number) => {
			const status = persistedModel.itemsMap[id];
			return status && status.color;
		},
		[persistedModel]
	)
}

export const useGetStatusStyleCallback = <P extends StatusResponse>(persistedModel: PersistItemsReducer<P>) => {
	return useCallback(
		(id: number | undefined) => {
			if (id === undefined) {
				return {};
			}

			const status = persistedModel.itemsMap[id];
			const color = status?.color;
			if (!color) {
				return {};
			}

			const hexColor = colorToHex(color)!;
			return {
				color: hexColor,
				fontWeight: 'bold'
			}
		},
		[persistedModel]
	)
}

// pdf export library is implemented in a way it doesn't see our redux Provider, so this method is added in those cases
export const useFormattedCurrencyValueWithArgsCallback = (persistedCurrency: PersistItemsReducer<IdNameSymbolResponse>, persistedTenant: PersistValueReducer<TenantResponseModel>) => {
	return useCallback(
		(value: number | undefined, showCurrencySymbol?: boolean) => {
			const currencyId = persistedTenant.value.currencyId;
			const idName = persistedCurrency.itemsMap[currencyId];
			const symbol = showCurrencySymbol && idName && idName.symbol;
			const formattedValue = formatNumberUtil(value) || '';

			if (value !== undefined && symbol) {
				return `${formattedValue} ${symbol}`
			} else if (value !== undefined) {
				return formattedValue;
			} else {
				return ''
			}
		},
		[persistedCurrency, persistedTenant]
	)
}

export const useFormattedCurrencyValueCallback = () => {
	const persistedCurrency = useSelector((state: RootState) => state.persistedCurrency);
	const persistedTenant = useSelector((state: RootState) => state.persistedTenant);

	return useFormattedCurrencyValueWithArgsCallback(persistedCurrency, persistedTenant)
}

export const useFormattedCurrencyValueByIdCallback = () => {
	const persistedCurrency = useSelector((state: RootState) => state.persistedCurrency);

	return useCallback(
		(value: number | undefined, currencyId: number | undefined) => {
			const idName = currencyId ? persistedCurrency.itemsMap[currencyId] : undefined;
			const symbol = idName && idName.symbol;
			const formattedValue = formatNumberUtil(value);

			if (value !== undefined && symbol) {
				return formattedValue;
			} else if (value !== undefined) {
				return formattedValue;
			} else {
				return ''
			}
		},
		[persistedCurrency]
	)
}

export const useActiveTeamMembersMemo = (
	persistedTeamMember: PersistProjectItemsReducer<ProjectTeamMemberResponse>,
	persistedUser: PersistActiveItemsReducer<UserModel>,
	projectId: number | undefined,
	userId?: number,
	userIds?: number[],
	permissionModuleName?: string,
	permissionName?: string
) => {
	const getUserId = useUserIdCallback(persistedTeamMember);

	useEffect(
		() => {
			if (projectId && !persistedTeamMember.projectMap[projectId]) {
				persistTeamMembersAction(projectId);
			}
		},
		[persistedTeamMember, projectId]
	)

	return useMemo(
		() => {
			if (!projectId) {
				return [];
			}

			let aciveTeamMembers = persistedTeamMember.projectMap[projectId]?.items.filter(tm => tm.isActive);
			if (permissionModuleName && permissionName) {
				aciveTeamMembers = aciveTeamMembers?.filter(x =>
					x.permissions?.isProjectManager ||
					x.permissions?.isSubstituteProjectManager ||
					(x.permissions as any)?.[permissionModuleName]?.[permissionName]
				);
			}
			const activeTeamMemberUsers = aciveTeamMembers?.map(tm => persistedUser.activeItemsMap[getUserId(tm.id, projectId)!]) || [];
			const filteredActiveTeamMemberUsers = removeUndefinedItemsFromArray(activeTeamMemberUsers);

			// user can be blocked in meantime, so we need to find it and display it in Select
			if (userId) {
				const user = persistedUser.itemsMap[userId];
				if (user) {
					if (!filteredActiveTeamMemberUsers.find(item => item.id === user.id)) {
						filteredActiveTeamMemberUsers.push(user);
					}
				}
			}

			// users can be blocked...
			if (userIds && userIds.length > 0) {
				for (const userId of userIds) {
					const user = persistedUser.itemsMap[userId];
					if (user) {
						if (!filteredActiveTeamMemberUsers.find(item => item.id === user.id)) {
							filteredActiveTeamMemberUsers.push(user);
						}
					}
				}
			}

			return filteredActiveTeamMemberUsers;
		},
		[persistedTeamMember.projectMap, getUserId, persistedUser, projectId, userId, userIds, permissionModuleName, permissionName]
	)
}

export const useFormattedProjectIdNameCallback = (persistedProject: PersistItemsReducer<ProjectResponse>) => {
	return useCallback(
		(id: number) => {
			const project = persistedProject.itemsMap[id];
			return project ?
				`${getFormatedId(EntityPrefixEnum.PROJECT, id)} - ${project.name}` :
				getFormatedId(EntityPrefixEnum.PROJECT, id);
		},
		[persistedProject]
	)
}
